import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ANNOUNCEMENT_AUDIENCE_LABELS,
  ANNOUNCEMENT_KIND_LABELS,
  IN_APP_MODULES,
  MODULE_LABELS,
  type AnnouncementAudience,
  type AnnouncementDTO,
  type AnnouncementInput,
  type AnnouncementKind,
  type InAppModule,
  type PhotoUploadTicket,
} from '@bifeai/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bifeai/ui';
import { ImagePlus, Megaphone, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { useCan } from '../lib/session';
import { formatDateTime } from '../lib/format';

const KIND_TONE: Record<AnnouncementKind, 'info' | 'warning' | 'success' | 'danger'> = {
  UPDATE: 'info',
  SERVICE: 'warning',
  NEWS: 'success',
  PROMO: 'danger',
};
const KIND_HINT: Record<AnnouncementKind, string> = {
  UPDATE: 'Mejoras y funciones nuevas del sistema.',
  SERVICE: 'Cortes o mantenimientos programados: además de la campanita, se ve una barra arriba en el sistema hasta que termina.',
  NEWS: 'Productos o servicios nuevos de BifeAI.',
  PROMO: 'Ofertas y ventas. Usá "Solo a quienes no tienen" para ofrecer un módulo a quien no lo compró.',
};

/** Fecha ISO → valor de <input type="datetime-local"> en hora local. */
const toLocal = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocal = (v: string) => (v ? new Date(v).toISOString() : null);

function statusOf(a: AnnouncementDTO): { label: string; tone: 'success' | 'neutral' | 'info' } {
  const now = Date.now();
  if (new Date(a.publishAt).getTime() > now) return { label: 'Programado', tone: 'info' };
  if (a.expiresAt && new Date(a.expiresAt).getTime() <= now) return { label: 'Vencido', tone: 'neutral' };
  return { label: 'Publicado', tone: 'success' };
}

/** Comunicación con los clientes: aparece en la campanita del sistema. */
export function CommsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<AnnouncementDTO | 'new' | null>(null);
  const can = useCan();
  const manage = can('comms.manage');
  const canDelete = can('comms.delete');
  const list = useQuery({ queryKey: ['announcements'], queryFn: () => api.get<AnnouncementDTO[]>('/platform/announcements') });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/platform/announcements/${id}`),
    onSuccess: async () => {
      toast.success('Anuncio borrado');
      await qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

  return (
    <>
      <PageHeader
        title="Comunicación"
        subtitle="Actualizaciones, avisos de servicio, novedades y promociones. Les llegan a la campanita del sistema."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')} disabled={!manage}>
            Nuevo anuncio
          </Button>
        }
      />
      {list.isPending ? (
        <Skeleton style={{ height: 200 }} />
      ) : !list.data?.length ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<Megaphone />}
              title="Sin anuncios"
              text="Contales a tus clientes las novedades, avisá un corte o lanzá una promoción."
            />
          </CardBody>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {list.data.map((a) => {
            const st = statusOf(a);
            return (
              <Card key={a.id}>
                <CardBody>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    {a.imageUrl && (
                      <img src={a.imageUrl} alt="" style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Badge tone={KIND_TONE[a.kind]}>{ANNOUNCEMENT_KIND_LABELS[a.kind]}</Badge>
                        <Badge tone={st.tone} dot>
                          {st.label}
                        </Badge>
                        <strong>{a.title}</strong>
                      </div>
                      <span className="bf-cell-sub" style={{ whiteSpace: 'pre-line' }}>
                        {a.body}
                      </span>
                      <span className="bf-cell-sub">
                        {ANNOUNCEMENT_AUDIENCE_LABELS[a.audience]}
                        {a.withoutModule ? ` · sin ${MODULE_LABELS[a.withoutModule]}` : ''}
                        {a.ownersOnly ? ' · solo dueños' : ' · todo el equipo'} · desde {formatDateTime(a.publishAt)}
                        {a.expiresAt ? ` hasta ${formatDateTime(a.expiresAt)}` : ''} · leído por {a.reads ?? 0}
                        {a.reads === 1 ? ' usuario' : ' usuarios'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Button size="sm" variant="ghost" aria-label="Editar" icon={<Pencil />} onClick={() => setEditing(a)} disabled={!manage} />
                      <Button size="sm" variant="ghost" aria-label="Borrar" icon={<Trash2 />} onClick={() => del.mutate(a.id)} disabled={!canDelete} />
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}
      {editing && (
        <Editor
          item={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            toast.success('Anuncio guardado');
            await qc.invalidateQueries({ queryKey: ['announcements'] });
          }}
        />
      )}
    </>
  );
}

function Editor({ item, onClose, onSaved }: { item: AnnouncementDTO | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const [v, setV] = useState({
    kind: (item?.kind ?? 'UPDATE') as AnnouncementKind,
    title: item?.title ?? '',
    body: item?.body ?? '',
    imageKey: item?.imageKey ?? null,
    imageUrl: item?.imageUrl ?? null,
    priceText: item?.priceText ?? '',
    ctaLabel: item?.ctaLabel ?? '',
    ctaUrl: item?.ctaUrl ?? '',
    audience: (item?.audience ?? 'ALL') as AnnouncementAudience,
    withoutModule: (item?.withoutModule ?? '') as InAppModule | '',
    ownersOnly: item?.ownersOnly ?? false,
    publishAt: toLocal(item?.publishAt),
    expiresAt: toLocal(item?.expiresAt),
    serviceStart: toLocal(item?.serviceStart),
    serviceEnd: toLocal(item?.serviceEnd),
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((s) => ({ ...s, [k]: val }));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File) => {
    setError(null);
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return setError('La imagen tiene que ser JPG, PNG o WebP.');
    if (file.size > 3 * 1024 * 1024) return setError('La imagen pesa más de 3 MB.');
    setUploading(true);
    try {
      const t = await api.post<PhotoUploadTicket>('/platform/announcements/image', { contentType: file.type, size: file.size });
      const res = await fetch(t.uploadUrl, { method: 'PUT', headers: t.headers, body: file, credentials: 'omit' });
      if (!res.ok) throw new Error('upload');
      setV((s) => ({ ...s, imageKey: t.key, imageUrl: URL.createObjectURL(file) }));
    } catch {
      setError('No se pudo subir la imagen. Probá de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setError(null);
    setSaving(true);
    const body: AnnouncementInput = {
      kind: v.kind,
      title: v.title,
      body: v.body,
      imageKey: v.imageKey,
      priceText: v.priceText || null,
      ctaLabel: v.ctaLabel || null,
      ctaUrl: v.ctaUrl || null,
      audience: v.audience,
      withoutModule: v.withoutModule || null,
      ownersOnly: v.ownersOnly,
      publishAt: fromLocal(v.publishAt),
      expiresAt: fromLocal(v.expiresAt),
      serviceStart: v.kind === 'SERVICE' ? fromLocal(v.serviceStart) : null,
      serviceEnd: v.kind === 'SERVICE' ? fromLocal(v.serviceEnd) : null,
    };
    try {
      if (item) await api.put(`/platform/announcements/${item.id}`, body as Record<string, unknown>);
      else await api.post('/platform/announcements', body as Record<string, unknown>);
      await onSaved();
    } catch (err) {
      const e = err as { message?: string; fields?: Record<string, string> };
      setError(e.fields ? Object.values(e.fields).join(' · ') : (e.message ?? 'No se pudo guardar.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={item ? 'Editar anuncio' : 'Nuevo anuncio'} wide>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Tipo" hint={KIND_HINT[v.kind]}>
          <Select value={v.kind} onChange={(e) => set('kind', e.target.value as AnnouncementKind)}>
            {(Object.keys(ANNOUNCEMENT_KIND_LABELS) as AnnouncementKind[]).map((k) => (
              <option key={k} value={k}>
                {ANNOUNCEMENT_KIND_LABELS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Título">
          <Input value={v.title} maxLength={120} onChange={(e) => set('title', e.target.value)} placeholder="Llegó Asistencia del personal" />
        </Field>
        <Field label="Mensaje">
          <Textarea rows={4} value={v.body} maxLength={2000} onChange={(e) => set('body', e.target.value)} />
        </Field>
        {v.kind === 'SERVICE' && (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <Field label="Corte desde">
              <Input type="datetime-local" value={v.serviceStart} onChange={(e) => set('serviceStart', e.target.value)} />
            </Field>
            <Field label="Corte hasta">
              <Input type="datetime-local" value={v.serviceEnd} onChange={(e) => set('serviceEnd', e.target.value)} />
            </Field>
          </div>
        )}
        <Field label="Imagen (opcional)" hint="JPG, PNG o WebP, hasta 3 MB. Horizontal queda mejor.">
          {v.imageUrl ? (
            <div style={{ position: 'relative', justifySelf: 'start' }}>
              <img src={v.imageUrl} alt="" style={{ maxWidth: 320, maxHeight: 160, borderRadius: 10, display: 'block' }} />
              <Button
                size="sm"
                variant="secondary"
                aria-label="Quitar imagen"
                icon={<X />}
                onClick={() => setV((s) => ({ ...s, imageKey: null, imageUrl: null }))}
              />
            </div>
          ) : (
            <label className="bf-btn" style={{ justifySelf: 'start', cursor: 'pointer' }}>
              <ImagePlus size={16} aria-hidden /> {uploading ? 'Subiendo…' : 'Elegir imagen'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])}
              />
            </label>
          )}
        </Field>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <Field label="Precio (opcional)">
            <Input value={v.priceText} maxLength={60} placeholder="USD 150" onChange={(e) => set('priceText', e.target.value)} />
          </Field>
          <Field label="Texto del botón (opcional)">
            <Input value={v.ctaLabel} maxLength={40} placeholder="Sumarlo" onChange={(e) => set('ctaLabel', e.target.value)} />
          </Field>
          <Field label="Link del botón" hint="/plan lleva a Mi plan. O un link https://…">
            <Input value={v.ctaUrl} maxLength={500} placeholder="/plan" onChange={(e) => set('ctaUrl', e.target.value)} />
          </Field>
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Para">
            <Select value={v.audience} onChange={(e) => set('audience', e.target.value as AnnouncementAudience)}>
              {(Object.keys(ANNOUNCEMENT_AUDIENCE_LABELS) as AnnouncementAudience[]).map((k) => (
                <option key={k} value={k}>
                  {ANNOUNCEMENT_AUDIENCE_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Solo a quienes no tienen">
            <Select value={v.withoutModule} onChange={(e) => set('withoutModule', e.target.value as InAppModule | '')}>
              <option value="">— (a todos)</option>
              {IN_APP_MODULES.map((m) => (
                <option key={m} value={m}>
                  {MODULE_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Lo ven">
            <Select value={v.ownersOnly ? 'owners' : 'all'} onChange={(e) => set('ownersOnly', e.target.value === 'owners')}>
              <option value="all">Todo el equipo</option>
              <option value="owners">Solo los dueños</option>
            </Select>
          </Field>
        </div>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
          <Field label="Publicar" hint="Vacío: ahora.">
            <Input type="datetime-local" value={v.publishAt} onChange={(e) => set('publishAt', e.target.value)} />
          </Field>
          <Field label="Deja de mostrarse" hint={v.kind === 'SERVICE' ? 'Vacío: cuando termina el corte.' : 'Vacío: no vence.'}>
            <Input type="datetime-local" value={v.expiresAt} onChange={(e) => set('expiresAt', e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} disabled={uploading} onClick={() => void save()}>
            {item ? 'Guardar' : 'Publicar'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
